import { SerializedError } from '@reduxjs/toolkit'

// TODO: Move to wildcat-types — the reservation service's guest API (/guest/v1)

/** 0 Required, 1 Optional, 2 Off */
export enum GuestFormFieldMode {
  Required = 0,
  Optional = 1,
  Off = 2,
}

/** 0 None, 1 Deposit, 2 PrepaidPackage */
export enum GuestBookingGuarantee {
  None = 0,
  Deposit = 1,
  PrepaidPackage = 2,
}

export enum ReservationStatus {
  Pending = 0,
  Confirmed = 1,
  Seated = 2,
  Completed = 3,
  Cancelled = 4,
  NoShow = 5,
  PendingReview = 6,
  WaitingForTime = 7,
  Expired = 8,
  Inquiry = 9,
  Arrived = 10,
}

export type GuestCancellationPolicy = {
  freeCancellationHours: number
  cancellationFeeAmount: number
}

export type GuestBookingTypeUpsell = {
  id: string
  productId: string
  productVariantId: string
  name: string
  unitPrice: number
  maximumQuantity: number
  prepaid: boolean
}

export type GuestBookingTypePackage = {
  id: string
  name: string
  pricePerGuest: number
  prepaid: boolean
}

export type GuestBookingType = {
  id: string
  bookingWindow: {
    bookingHorizonDays: number
    minimumNoticeMinutes: number
  } | null
  name: string
  description: string | null
  sellingText: string | null
  welcomeText: string | null
  seatingNote: string | null
  minimumPartySize: number
  maximumPartySizeOnline: number
  sortOrder: number
  oversizeInquiryEnabled: boolean
  waitlistEnabled: boolean
  waitlistPartyCap: number | null
  waitlistOfferMinutes: number
  cancellationPolicyOverride: GuestCancellationPolicy | null
  guarantee: GuestBookingGuarantee
  depositAmountPerGuest: number
  depositRequiredFromPartySize: number | null
  packages: GuestBookingTypePackage[] | null
  upsells: GuestBookingTypeUpsell[] | null
}

export type GuestWidget = {
  tenantId: string
  outletId: string
  name: string
  timeZoneId: string
  languageCode: string
  currency: string
  /** 1 Adyen, 2 Surfboard, null when the outlet takes no online payment */
  paymentProvider: 1 | 2 | null
  phone: string | null
  email: string | null
  cancellationPolicy: GuestCancellationPolicy
  reservationFee: number
  holdMinutes: number
  welcomeText: string | null
  heroImageUrl: string | null
  logoUrl: string | null
  slug: string | null
  widgetKey: string
  formFields: {
    email: GuestFormFieldMode
    request: GuestFormFieldMode
    companyInvoice: GuestFormFieldMode
  } | null
  bookingTypes: GuestBookingType[]
}

export type RestaurantFinderBookingType = {
  bookingTypeId: string
  name: string
  /** UTC, the first free arrival inside the searched window */
  startsAt: string | null
  waitlistEligible: boolean
  /** UTC, set only when `startsAt` is null */
  nextFreeAt: string | null
}

export type GuestRestaurant = {
  tenantId: string
  outletId: string
  name: string
  city: string | null
  timeZoneId: string
  widgetKey: string
  branding: {
    heroImageUrl: string | null
    logoUrl: string | null
    welcomeText: string | null
  } | null
  bookingTypes: RestaurantFinderBookingType[]
}

export type GuestSeating = {
  /** UTC */
  startsAt: string
  /** UTC */
  endsAt: string
  sharedTable: boolean
  discount: { name: string } | null
  /** Asked for, not booked: the restaurant arranges this arrival personally */
  inquiry: boolean
}

export type GuestAvailability = {
  seatings: GuestSeating[]
  waitlistEligible: boolean
}

export type GuestReservationQuote = {
  currency: string
  depositAmount: number
  reservationFee: number
  collectAtBooking: boolean
}

export type GuestReservation = {
  id: string
  referenceCode: string
  bookingTypeId: string
  businessDate: string
  startsAt: string | null
  endsAt: string | null
  partySize: number
  status: ReservationStatus
  created: string
  cancelledAt: string | null
  guestName: string | null
  guestEmail: string | null
  guestPhone: string | null
  specialRequest: string | null
  sharedTable: boolean | null
  discountDisplayName: string | null
  quote: GuestReservationQuote | null
  holdExpiresAt: string | null
  paymentDueAt: string | null
  package: {
    id: string
    name: string
    pricePerGuest: number
    guestCount: number
    prepaid: boolean
  } | null
  upsells: {
    items: {
      id: string
      name: string
      quantity: number
      unitPrice: number
      prepaid: boolean
    }[]
  } | null
  cancellationPolicy: GuestCancellationPolicy | null
}

export type GuestReservationCheck = {
  checkId: string
  currency: string
  /** What the booking still has to collect */
  bookingAmountDue: number
}

export type GuestReservationResponse = {
  reservation: GuestReservation
  /** Set once the booking is submitted */
  visitUrl: string | null
  /** Null until Core has created the check, shortly after Confirm */
  check: GuestReservationCheck | null
}

export type GuestUpsellSelection = {
  upsellId: string
  quantity: number
}

export type GuestHoldRequest = {
  idempotencyKey: string
  booking: {
    bookingTypeId: string
    businessDate: string
    /** UTC */
    startsAt: string
    partySize: number
    packageId?: string | null
    upsells?: GuestUpsellSelection[]
  }
}

/** Order Web's verified path: the signed-in company session, never a lookup */
export type GuestCompanyInput = {
  companyId: string
  memberId: string
  invoiceReference?: string | null
}

export type GuestDetailsRequest = {
  idempotencyKey: string
  guestName: string
  guestEmail: string | null
  guestPhone: string | null
  guestLanguageCode: string | null
  specialRequest: string | null
  extras?: {
    packageId?: string | null
    upsells?: GuestUpsellSelection[]
  } | null
  company?: GuestCompanyInput | null
}

export type GuestWaitlistJoinRequest = {
  idempotencyKey: string
  bookingTypeId: string
  businessDate: string
  /** UTC, inclusive */
  preferredStartsAt: string
  /** UTC, inclusive */
  preferredEndsAt: string
  partySize: number
  guestName: string
  guestEmail: string | null
  guestPhone: string
  guestLanguageCode: string | null
}

export type GuestWaitlistEntry = {
  id: string
  bookingTypeId: string
  businessDate: string
  preferredStartsAt: string
  preferredEndsAt: string
  partySize: number
  guestPhone: string
}

export type GuestWaitlistResponse = {
  entry: GuestWaitlistEntry
}

/** One of the signed-in guest's bookings, from `GET Reservations` */
export type GuestReservationHistoryItem = {
  id: string
  tenantId: string
  outletId: string
  referenceCode: string
  businessDate: string
  /** UTC */
  startsAt: string | null
  /** UTC */
  endsAt: string | null
  partySize: number
  /** The stored status; the booking's own read has the live payment state */
  status: ReservationStatus
  /** Null once the link has expired or been revoked */
  visitUrl: string | null
}

export type GuestHistoryOutlet = {
  id: string
  tenantId: string
  name: string
  timeZoneId: string
  logoUrl: string | null
  imageUrl: string | null
}

export type GuestReservationHistoryResponse = {
  reservations: GuestReservationHistoryItem[]
  /** Total matches before paging */
  count: number
  /** Null unless `Extent.Outlets` was asked for */
  outlets: GuestHistoryOutlet[] | null
}

/** Domain errors carry a kebab-case code in `message` */
export type ReservationAPIError = {
  message: string
  errorCode?: string
  additionalData?: unknown
}

// Local types

export type TableFinderQuery = {
  city: string
  /** yyyy-MM-dd */
  date: string
  guests: string
  /** HH:mm */
  time: string
}

export type TableFinderParams = {
  city: string
  /** yyyy-MM-dd */
  date: string
  guests: number
  /** HH:mm */
  time: string
}

export type TableFinderResult = {
  restaurant: GuestRestaurant
  /** The booking type the times below belong to, null when fully booked */
  bookingTypeId: string | null
  /** Up to three UTC arrivals nearest the requested time, chronological */
  times: string[]
  /** UTC, the earliest of `times` */
  nextAvailableAt: string | null
  /** The booking type to join the waitlist for, when fully booked */
  waitlistBookingTypeId: string | null
}

export enum BookingStep {
  Type = 'type',
  Party = 'party',
  Time = 'time',
  Details = 'details',
  Confirm = 'confirm',
  Confirmed = 'confirmed',
}

export enum BookingWriteKind {
  Hold = 'hold',
  Details = 'details',
  Confirm = 'confirm',
  Waitlist = 'waitlist',
}

export type BookPageQuery = {
  widgetKey: string
  bookingTypeId?: string
  /** yyyy-MM-dd */
  date?: string
  guests?: string
  /** UTC arrival from a finder chip */
  startsAt?: string
  waitlist?: string
}

export type VisitListItem = {
  reservation: GuestReservationHistoryItem
  /** Null when the restaurant's projection is missing */
  outlet: GuestHistoryOutlet | null
  /** Still ahead when the list was read; set then so render never reads the clock */
  isUpcoming: boolean
}

export type BookingReducerState = {
  finderParams: TableFinderParams | null
  finderResults: TableFinderResult[]
  finderFetchPending: boolean
  finderError: SerializedError | null
  /** The search whose answer is wanted; older answers are dropped */
  finderRequestId: string | null

  widget: GuestWidget | null
  widgetFetchPending: boolean
  widgetError: SerializedError | null

  step: BookingStep
  bookingTypeId: string | null
  partySize: number
  /** yyyy-MM-dd, the outlet's business day */
  businessDate: string | null
  /** UTC arrival picked on the time step */
  selectedStartsAt: string | null
  /** Required when the booking type has packages */
  packageId: string | null

  availability: GuestAvailability | null
  availabilityFetchPending: boolean
  availabilityError: SerializedError | null
  /** The fetch whose answer is wanted; cleared when the choices change */
  availabilityRequestId: string | null

  upsellSelections: GuestUpsellSelection[]
  specialRequest: string
  isCompanyBooking: boolean

  reservation: GuestReservationResponse | null
  holdLost: boolean

  waitlistEntry: GuestWaitlistEntry | null

  writePending: BookingWriteKind | null
  writeError: SerializedError | null
  idempotencyKeys: Partial<Record<BookingWriteKind, string>>

  visits: VisitListItem[]
  visitsFetchPending: boolean
}
